import { Tree, type TreeProps } from 'antd';
import { type ReactNode, useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { DraggableTreeNodeTitle } from './DraggableTreeNodeTitle.tsx';
import {
  type IDraggableTreeDropInfo,
  type IDraggableTreeNode,
  type TreeDropEvent,
} from './draggableTreeTypes.ts';
import { applyDropToTree, type CanDropFn } from './draggableTreeUtils.ts';

const TreeWrapper = styled.div`
  .ant-tree-treenode.drop-target {
    background-color: ${props => props.theme.colorPrimaryBg};
    border-radius: ${props => props.theme.borderRadiusSM}px;
  }

  .ant-tree-treenode.drop-target ~ .ant-tree-drop-indicator {
    display: none;
  }
`;

function dropIndicatorRender({ dropPosition }: { dropPosition: -1 | 0 | 1 }) {
  if (dropPosition === 0) return null;
  return undefined;
}

export interface IDraggableTreeProps<TData = unknown>
  extends Omit<
    TreeProps,
    | 'treeData'
    | 'draggable'
    | 'onDrop'
    | 'titleRender'
    | 'expandedKeys'
    | 'onExpand'
  > {
  /** Dati dell'albero */
  treeData: IDraggableTreeNode<TData>[];

  /** Chiavi dei nodi inizialmente espansi (modo uncontrolled) */
  defaultExpandedKeys?: string[];

  /** Chiavi espanse controllate dall'esterno */
  expandedKeys?: string[];

  /** Callback per cambio espansione (modo controlled) */
  onExpandedKeysChange?: (keys: string[]) => void;

  /**
   * Callback invocato dopo un drop valido.
   * Riceve l'albero aggiornato e le info sul drop.
   */
  onTreeDrop?: (
    newTree: IDraggableTreeNode<TData>[],
    info: IDraggableTreeDropInfo<TData>,
  ) => void;

  /**
   * Escape hatch: gestione drop personalizzata (bypassa `applyDropToTree`).
   * Se fornito, `onTreeDrop` e `canDrop` vengono ignorati.
   */
  onDropRaw?: (info: TreeDropEvent) => void;

  /**
   * Validazione del drop.
   * Se non fornito, i drop dentro nodi foglia vengono rifiutati.
   */
  canDrop?: CanDropFn<TData>;

  /**
   * Callback modifica nodo.
   * Se fornito, ogni nodo mostra il bottone "Modifica".
   */
  onEdit?: (node: IDraggableTreeNode<TData>) => void;

  /**
   * Callback eliminazione nodo.
   * Se fornito, ogni nodo mostra il bottone "Elimina".
   */
  onDelete?: (node: IDraggableTreeNode<TData>) => void;

  /**
   * Icona per ogni nodo.
   * Può essere un ReactNode fisso o una funzione per icona diversa per nodo.
   */
  nodeIcon?: ReactNode | ((node: IDraggableTreeNode<TData>) => ReactNode);

  /**
   * Azioni extra per ogni nodo (bottoni aggiuntivi oltre edit/delete).
   * Restituisce un ReactNode o undefined per non mostrare nulla.
   */
  nodeExtraActions?: (node: IDraggableTreeNode<TData>) => ReactNode;

  /**
   * Validazione del drop a livello Ant Design Tree.
   * Se non fornito, il default rifiuta drop dentro nodi foglia.
   */
  allowDrop?: TreeProps['allowDrop'];
}

export function DraggableTree<TData = unknown>(
  props: IDraggableTreeProps<TData>,
) {
  const {
    treeData,
    defaultExpandedKeys,
    expandedKeys: controlledExpandedKeys,
    onExpandedKeysChange,
    onTreeDrop,
    onDropRaw,
    canDrop,
    onEdit,
    onDelete,
    nodeIcon,
    nodeExtraActions,
    allowDrop,
    ...restProps
  } = props;

  const [internalExpandedKeys, setInternalExpandedKeys] = useState<string[]>(
    () => defaultExpandedKeys ?? [],
  );

  const isControlled = controlledExpandedKeys !== undefined;
  const expandedKeys = isControlled
    ? controlledExpandedKeys
    : internalExpandedKeys;

  const handleExpand = useCallback(
    (keys: React.Key[]) => {
      const stringKeys = keys as string[];
      if (!isControlled) {
        setInternalExpandedKeys(stringKeys);
      }
      onExpandedKeysChange?.(stringKeys);
    },
    [isControlled, onExpandedKeysChange],
  );

  const resolvedTitleRender = useMemo(() => {
    if (!onEdit && !onDelete && !nodeIcon && !nodeExtraActions) {
      return undefined;
    }

    return (node: IDraggableTreeNode<TData>) => {
      const icon = typeof nodeIcon === 'function' ? nodeIcon(node) : nodeIcon;
      return (
        <DraggableTreeNodeTitle
          icon={icon}
          title={node.title}
          onEdit={onEdit ? () => onEdit(node) : undefined}
          onDelete={onDelete ? () => onDelete(node) : undefined}
          extraActions={nodeExtraActions?.(node)}
        />
      );
    };
  }, [onEdit, onDelete, nodeIcon, nodeExtraActions]);

  const handleDrop: TreeProps['onDrop'] = useCallback(
    (info: TreeDropEvent) => {
      if (onDropRaw) {
        onDropRaw(info);
        return;
      }

      const dragKey = info.dragNode.key as string;
      const dropKey = info.node.key as string;

      const newTree = applyDropToTree({
        treeData,
        dragKey,
        dropKey,
        dropToGap: info.dropToGap,
        dropPosition: info.dropPosition,
        canDrop,
      });
      if (!newTree) return;

      const flatNodes = treeData.flatMap(function flatten(
        node: IDraggableTreeNode<TData>,
      ): IDraggableTreeNode<TData>[] {
        return [node, ...(node.children?.flatMap(flatten) ?? [])];
      });
      const dragNode = flatNodes.find(node => node.key === dragKey);
      const dropNode = flatNodes.find(node => node.key === dropKey);

      if (dragNode && dropNode) {
        onTreeDrop?.(newTree, {
          dragNode,
          dropNode,
          dropToGap: info.dropToGap,
          dropPosition: info.dropPosition,
        });
      }
    },
    [treeData, onDropRaw, onTreeDrop, canDrop],
  );

  return (
    <TreeWrapper>
      <Tree
        draggable
        blockNode
        {...restProps}
        treeData={treeData}
        expandedKeys={expandedKeys}
        onExpand={handleExpand}
        onDrop={handleDrop}
        allowDrop={
          allowDrop ??
          (({ dropNode, dropPosition }) => {
            if (dropPosition === 0) return !dropNode.isLeaf;
            return true;
          })
        }
        dropIndicatorRender={dropIndicatorRender}
        titleRender={resolvedTitleRender as TreeProps['titleRender']}
        fieldNames={{ key: 'key', title: 'title', children: 'children' }}
      />
    </TreeWrapper>
  );
}
